/**
 * Locked avatar palette — flat-shaded matte-clay space explorer.
 * Authored ONCE as sRGB hex (what a designer / colour-picker shows).
 *
 * THE VERTEX-COLOUR LANDMINE (three r169, ColorManagement ON):
 *   material.color.setHex(hex) decodes sRGB -> linear for you, but a raw
 *   value written into a `color` BufferAttribute is read by the shader AS
 *   LINEAR (no decode). So the SAME hex used as a material tint vs. a vertex
 *   colour renders DIFFERENTLY unless we decode once, HERE.
 *     PALETTE_HEX : raw 0xRRGGBB sRGB int  -> ONLY for material.color.setHex()
 *     PALETTE     : THREE.Color already linear -> code that wants a Color
 *     PALETTE_RGB / writeColor : linear [r,g,b] -> for colour BufferAttributes
 *   Vertex-colour writers MUST use PALETTE_RGB / writeColor, never PALETTE_HEX.
 */
import * as THREE from "three";

export const PALETTE_HEX = {
  clay: 0xb8b2a6, // body/suit base (suit verts authored WHITE; this is the default tint)
  shadow: 0x4a4f59, // dark detail: joints, pelvis, pack, gun body
  rust: 0xc06a4a, // matte strap/fabric accent (NOT emissive)
  steel: 0x8a929e, // cool plate / hardware
  visorGlow: 0x4fd8ff, // emissive visor / trim (self-lit; default accent)
  jetGlow: 0x6aa8ff, // jet exhaust sprite tint (already additive)
  skin: 0x9a7b63, // optional exposed under-suit
} as const;

export type PaletteKey = keyof typeof PALETTE_HEX;

// The single decode point: ONE sRGB -> linear. Do NOT also call
// convertSRGBToLinear() — setHex(hex, SRGBColorSpace) already decodes;
// chaining would double-decode and render too dark.
const toLinear = (hex: number): THREE.Color =>
  new THREE.Color().setHex(hex, THREE.SRGBColorSpace);

export const PALETTE: Record<PaletteKey, THREE.Color> = Object.fromEntries(
  Object.entries(PALETTE_HEX).map(([k, hex]) => [k, toLinear(hex)]),
) as Record<PaletteKey, THREE.Color>;

export const PALETTE_RGB: Record<PaletteKey, [number, number, number]> =
  Object.fromEntries(
    Object.entries(PALETTE).map(([k, c]) => [k, [c.r, c.g, c.b]]),
  ) as Record<PaletteKey, [number, number, number]>;

/** Write a palette colour into a Float32 colour buffer at vertex i (linear). */
export function writeColor(buf: Float32Array, i: number, key: PaletteKey): void {
  const c = PALETTE_RGB[key];
  buf[i * 3] = c[0];
  buf[i * 3 + 1] = c[1];
  buf[i * 3 + 2] = c[2];
}

/** Fill an entire segment's colour buffer with one key (constant region). */
export function fillColor(count: number, key: PaletteKey): Float32Array {
  const buf = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) writeColor(buf, i, key);
  return buf;
}
